import { useEffect, useMemo, useState } from 'react'
import { groupBy, maxBy, sortBy, intersection } from 'lodash'

import LootStatisticsBar from './LootStatisticsBar'
import LootHistoryResponseTypeModal from './LootHistoryResponseTypeModal'
import { LootHistoryUrlOptions } from 'app/frontend/base/api/team'
import { ColumnDef, HeaderContext } from '@tanstack/react-table'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { Link } from 'react-router-dom'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamLootHistory } from 'app/frontend/base/config/queries'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CircleQuestionMark } from 'lucide-react'
import dayjs from 'dayjs'
import { useAuth } from 'app/frontend/base/config/context/auth'

const LootStatisticsTable = ({
  historyOptions,
}: {
  historyOptions: LootHistoryUrlOptions
}) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const [hiddenResponseTypes, setHiddenResponseTypes] = useState<number[]>(
    JSON.parse(localStorage.getItem('hiddenResponseTypes') || '[]'),
  )

  const { data, isPending } = useTeamLootHistory(historyOptions)

  useEffect(() => {
    localStorage.setItem(
      'hiddenResponseTypes',
      JSON.stringify(hiddenResponseTypes),
    )
  }, [hiddenResponseTypes])

  const averageForAwards = (
    awards: wowaudit.LootHistoryItem[],
    type: keyof wowaudit.LootHistoryItem,
  ) => {
    const filteredAwards = awards.filter((award) => award[type] !== null)

    if (filteredAwards.length === 0) {
      return null
    } else {
      return (
        filteredAwards.reduce((acc, award) => {
          return acc + (award[type] as number)
        }, 0) / filteredAwards.length
      )
    }
  }

  const columns: ColumnDef<wowaudit.TeamMembership>[] = useMemo(() => {
    const filteredItems = sortBy(
      data?.history_items.filter(
        (item) =>
          !item.discarded &&
          !item.propagated_reponse_type.excluded &&
          !hiddenResponseTypes.includes(item.propagated_reponse_type.id),
      ),
      (item) =>
        data?.loot_history_response_type_order.indexOf(
          item.propagated_reponse_type.id,
        ),
    )
    const awardsByCharacter = groupBy(filteredItems, 'character_id')
    const maxSize = Math.max(
      ...Object.values(awardsByCharacter).map((awards) => awards.length),
    )
    const showCompetition = filteredItems.some(
      (item) => item.same_response_amount !== null,
    )
    const showWishValue = filteredItems.some((item) => item.wish_value !== null)

    return [
      {
        ...genericSorting<wowaudit.TeamMembership>('text', 'Character'),
        accessorKey: 'characterReference.name',
        id: 'name',
        size: 175,
        cell: (info) => {
          return (
            <div className='flex'>
              <Link
                to={info.row.original.characterReference.path}
                className='inline-flex gap-2 items-center'
              >
                <CharacterRoleIcon role={info.row.original.role} />
                <CharacterClassIcon
                  characterClass={
                    info.row.original.characterReference.characterClass
                  }
                />

                {info.row.original.characterReference.name}
              </Link>
            </div>
          )
        },
      },
      {
        size: 30,
        id: 'total',
        ...genericSorting<wowaudit.TeamMembership>(
          'alphanumeric',
          'Awarded',
          true,
        ),
        accessorFn: (row) =>
          (awardsByCharacter[row.characterReference.id] ?? []).length,
      },
      {
        id: 'awarded',
        header: '',
        cell: (info) => {
          const awardedItems =
            awardsByCharacter[info.row.original.characterReference.id] ?? []
          return (
            <LootStatisticsBar
              groupedAwards={groupBy(
                awardedItems,
                'propagated_reponse_type.id',
              )}
              maxSize={maxSize}
              categoryOrder={data?.loot_history_response_type_order ?? []}
            />
          )
        },
      },
      ...(showCompetition
        ? [
            {
              ...genericSorting<wowaudit.TeamMembership>(
                'alphanumeric',
                '',
                true,
              ),
              size: 50,
              header: ({
                column,
              }: HeaderContext<wowaudit.TeamMembership, unknown>) => (
                <Tooltip>
                  <TooltipTrigger className='mt-1'>
                    <CircleQuestionMark
                      size={15}
                      onClick={() => column.toggleSorting()}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side='left'
                    className='max-w-[15rem] font-normal'
                  >
                    <p className='mb-2'>
                      Average competition. Displays the average amount of other
                      people that had the same response for each awarded item.
                    </p>
                    <div className='text-muted-foreground'>
                      This is only available for items that are awarded when the
                      wowaudit addon is enabled.
                    </div>
                  </TooltipContent>
                </Tooltip>
              ),
              id: 'same_response_amount',
              accessorFn: (row: wowaudit.TeamMembership) => {
                const competition = averageForAwards(
                  awardsByCharacter[row.characterReference.id] || [],
                  'same_response_amount',
                )
                return competition === null ? '-' : competition.toFixed(1)
              },
            },
          ]
        : []),
      ...(showWishValue
        ? [
            {
              ...genericSorting<wowaudit.TeamMembership>(
                'alphanumeric',
                '',
                true,
              ),
              size: 100,
              header: ({
                column,
              }: HeaderContext<wowaudit.TeamMembership, unknown>) => (
                <span className='inline-flex items-center gap-1 whitespace-nowrap'>
                  <span
                    className='cursor-pointer'
                    onClick={() => column.toggleSorting()}
                  >
                    Value
                  </span>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleQuestionMark
                        size={15}
                        onClick={() => column.toggleSorting()}
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      side='left'
                      className='max-w-[15rem] font-normal whitespace-normal'
                    >
                      <p className='mb-2'>
                        Displays the average wishlist upgrade value of awarded
                        items.
                      </p>
                      <div className='text-muted-foreground'>
                        This is only available for items that are awarded when
                        the wowaudit addon is enabled.
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </span>
              ),
              id: 'wish_value',
              accessorFn: (row: wowaudit.TeamMembership) => {
                const wishValue = averageForAwards(
                  awardsByCharacter[row.characterReference.id] || [],
                  'wish_value',
                )
                return wishValue === null ? '-' : wishValue.toFixed(0)
              },
            },
          ]
        : []),
      {
        ...genericSorting<wowaudit.TeamMembership>('basic', 'Last loot', true),
        id: 'last_loot',
        sortingFn: (rowA, rowB) => {
          const lastLootA = maxBy(
            awardsByCharacter[rowA.original.characterReference.id] || [],
            (loot) => loot.awarded_at,
          )
          const lastLootB = maxBy(
            awardsByCharacter[rowB.original.characterReference.id] || [],
            (loot) => loot.awarded_at,
          )

          if (lastLootA && lastLootB) {
            return (
              dayjs(lastLootA.awarded_at).unix() -
              dayjs(lastLootB.awarded_at).unix()
            )
          } else if (lastLootA) {
            return 1
          } else if (lastLootB) {
            return -1
          } else {
            return 0
          }
        },
        size: 90,
        accessorFn: (row: wowaudit.TeamMembership) => {
          const lastLoot = maxBy(
            awardsByCharacter[row.characterReference.id] || [],
            (loot) => loot.awarded_at,
          )
          return lastLoot ? dayjs(lastLoot.awarded_at).format('MMM D') : 'Never'
        },
      },
    ]
  }, [data?.history_items, hiddenResponseTypes])

  const awardCountByType = useMemo(() => {
    const filteredItems =
      data?.history_items.filter((item) => !item.discarded) ?? []
    return groupBy(filteredItems, 'propagated_reponse_type.id')
  }, [data?.history_items])

  return (
    <div className='mb-16'>
      {Object.keys(awardCountByType).length ? (
        <div className='bg-muted rounded-md p-4 mb-2'>
          <div className='grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1'>
            {intersection(
              data?.loot_history_response_type_order,
              Object.keys(awardCountByType).map((typeId) => parseInt(typeId)),
            ).map((typeId) => {
              const type = data?.loot_history_response_types.find(
                (t) => t.id === typeId,
              )

              if (!type || type.excluded) {
                return null
              } else {
                return (
                  <div
                    key={type.id}
                    className={`select-none py-1 px-2 rounded-sm bg-accent flex items-center gap-3 cursor-pointer hover:bg-accent/80 transition-colors ${
                      hiddenResponseTypes.includes(type.id)
                        ? 'opacity-30 line-through'
                        : ''
                    }`}
                    onClick={() => {
                      if (hiddenResponseTypes.includes(type.id)) {
                        setHiddenResponseTypes(
                          hiddenResponseTypes.filter((t) => t !== type.id),
                        )
                      } else {
                        setHiddenResponseTypes([
                          ...hiddenResponseTypes,
                          type.id,
                        ])
                      }
                    }}
                  >
                    <div
                      className='w-4 h-4 min-w-[1rem] min-h-[1rem]'
                      style={{ backgroundColor: type.rgba }}
                    />
                    <span className='name grow'>{type.name}</span>
                    <span>{awardCountByType[type.id]?.length}</span>
                  </div>
                )
              }
            })}
          </div>
          <div className='flex mt-1.5 mb-[-0.5rem] flex-wrap justify-between items-center gap-2'>
            <div className='text-xs'>
              <span
                className='cursor-pointer hover:text-primary transition-colors'
                onClick={() => setHiddenResponseTypes([])}
              >
                show
              </span>{' '}
              /{' '}
              <span
                className='cursor-pointer hover:text-primary transition-colors'
                onClick={() =>
                  setHiddenResponseTypes(
                    data?.loot_history_response_types.map((t) => t.id) ?? [],
                  )
                }
              >
                hide
              </span>{' '}
              all
            </div>
            {accessLevel === 'admin' ? (
              <LootHistoryResponseTypeModal
                lootHistoryOptions={historyOptions}
                responseTypes={data?.loot_history_response_types ?? []}
                responseTypeOrder={data?.loot_history_response_type_order ?? []}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {isPending ? (
        <Skeleton className='h-[40rem] bg-muted rounded-md' />
      ) : (
        <DataTable
          key={JSON.stringify({
            ...historyOptions,
            hiddenResponseTypes,
          })}
          columns={columns}
          className='bg-muted'
          data={(team.members ?? []).filter(
            (member) => member.teamRank?.wishlist_visibility,
          )}
          includeHeader={true}
          getRowId={(row) => row.id.toString()}
          sortingState={[{ id: 'total', desc: true }]}
        />
      )}
    </div>
  )
}

export default LootStatisticsTable
